import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { z } from "zod";

import {
  formatWorklistFooterLinesForEditing,
  formatWorklistFooterLinesForStorage,
} from "@blis/shared/schemas/settings";
import { trpc } from "@blis/trpc/react";

import { Button } from "@/components/ui/button";
import { FieldError, Label, TextArea } from "@/components/ui/field";
import { toast } from "@/components/ui/toast";
import { useSettings } from "../../hooks";
import { SettingsGroup } from "./SettingsGroup";

const schema = z.object({
  worklistFooterLines: z.string(),
});

type FormValues = z.infer<typeof schema>;

function footerSettingToText(value: string | null | undefined) {
  return formatWorklistFooterLinesForEditing(value);
}

export function WorklistSettings() {
  const { settings } = useSettings();
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      worklistFooterLines: footerSettingToText(settings.worklistFooterLines),
    },
  });
  const { reset } = form;

  useEffect(() => {
    reset({
      worklistFooterLines: footerSettingToText(settings.worklistFooterLines),
    });
  }, [settings.worklistFooterLines, reset]);

  const mutation = trpc.settings.update.useMutation();

  const onSubmit = async (values: FormValues) => {
    const value = formatWorklistFooterLinesForStorage(
      values.worklistFooterLines,
    );
    await mutation.mutateAsync([{ name: "worklistFooterLines", value }], {
      onSuccess() {
        toast.success("Settings updated");
      },
      onError(err) {
        toast.error("Error updating settings", {
          description: err.message,
        });
      },
    });
  };

  return (
    <SettingsGroup
      title="Worklist printing"
      description="Each non-empty line appears after the orders on the last page of each printed worklist. Leave blank to omit the footer."
    >
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FormProvider {...form}>
          <Controller
            control={form.control}
            name="worklistFooterLines"
            render={({ field, fieldState: { error } }) => (
              <>
                <Label htmlFor="worklist-footer-lines">Footer lines</Label>
                <TextArea
                  id="worklist-footer-lines"
                  aria-label="Worklist footer lines"
                  className="min-h-24 font-mono text-sm"
                  {...field}
                />
                <FieldError>{error?.message}</FieldError>
              </>
            )}
          />
        </FormProvider>
        <div className="flex justify-end mt-3">
          <Button type="submit" isPending={mutation.isPending}>
            Save
          </Button>
        </div>
      </form>
    </SettingsGroup>
  );
}
